import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { log } from "../../lib/log";

// Postcard generation (once per open) + link/PNG/native share actions for ShareCardModal.
export function usePostcardShare(open, onClose) {
  const [loading, setLoading] = useState(false);
  const [postcard, setPostcard] = useState(null);
  const [copied, setCopied] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  // Phones default to the tall 4:5 card (big type, Instagram-ready); desktop to the wide OG card.
  const [format, setFormat] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? "portrait" : "wide"));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Generate once per open. Parents re-render often (timers/polling) and pass a fresh onClose
  // each time, so it must NOT be a dependency or the postcard re-generates in a loop.
  useEffect(() => {
    if (!open) {
      setPostcard(null);
      setCopied(false);
      setImgLoaded(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.post("/postcards");
        if (alive) setPostcard(res.data);
      } catch (e) {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Could not generate postcard. Run a scan first.");
        onCloseRef.current?.();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  // The link friends receive is the app's own domain (stargridastro.com in production) → /postcard/<slug>
  const siteBase = typeof window !== "undefined" ? window.location.origin : backend;
  const shareUrl = postcard ? `${siteBase}/postcard/${postcard.slug}` : "";
  const imageUrl = postcard ? `${backend}/api/postcards/${postcard.slug}.png${format === "portrait" ? "?format=portrait" : ""}` : "";
  const caption = postcard ? `My StarGrid blueprint reads ${postcard.score}/100 in ${postcard.city}. Reveal yours: ${shareUrl}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied ✨");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link");
    }
  };

  const getPngFile = async () => {
    const res = await fetch(imageUrl);
    const blob = await res.blob();
    return new File([blob], `stargrid-postcard-${postcard.slug}.png`, { type: "image/png" });
  };

  const downloadPng = async () => {
    try {
      const file = await getPngFile();
      // Phones: hand the PNG to the native share sheet (Instagram, Photos, Messages…)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `${postcard.name}'s StarGrid Blueprint`, text: caption });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
          log.debug("File share unavailable, falling back to download:", e?.name || e);
        }
      }
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Postcard PNG downloaded");
    } catch (e) {
      log.warn("Postcard download failed:", e);
      window.open(imageUrl, "_blank", "noopener");
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: `${postcard.name}'s StarGrid Blueprint`,
        text: caption.replace(shareUrl, "").trim(),
        url: shareUrl,
      });
    } catch (e) {
      log.debug("Native share dismissed:", e?.name || e);
    }
  };

  return { loading, postcard, copied, imgLoaded, setImgLoaded, format, setFormat, shareUrl, imageUrl, caption, getPngFile, downloadPng, copyLink, nativeShare };
}
